import { SEND_MONEY_ACTIONS } from "../constants/actionTypes";
import { clearCountryCurrency, saveCountryCurrency } from "../store/sendMoney/countryCurrencyPersist";
import { clearSelectedRecipient, saveSelectedRecipient } from "../store/sendMoney/selectedRecipientPersist";
import { clearSendMoneyLimits, saveSendMoneyLimits } from "../store/sendMoney/limitsPersist";
import { clearSendMoneyPersistence } from "../store/sendMoney/sendMoneyPersist";
import {
  MobileWalletOperator,
  SendMoneyLimitsContentCache,
  SendMoneyLimitsState,
  SendMoneyRecipientAccount,
  SendMoneyTransferMethodId,
} from "../types/sendMoney";

/**
 * Set the selected recipient for the send money flow
 */
export function SetSelectedRecipient(payload: SendMoneyRecipientAccount | null) {
  if (payload) {
    saveSelectedRecipient(payload);
  } else {
    clearSelectedRecipient();
  }
  return {
    type: SEND_MONEY_ACTIONS.SET_SELECTED_RECIPIENT,
    payload,
  };
}

/**
 * Set the send money limits state for limitDisplays, limits, and walletLimitsApply
 * (from the accounts call) and persist it to sessionStorage so it survives a
 * refresh. Null clears both the store and storage.
 */
export function SetSendMoneyLimits(payload: SendMoneyLimitsState | null) {
  if (payload) {
    saveSendMoneyLimits(payload);
  } else {
    clearSendMoneyLimits();
  }
  return {
    type: SEND_MONEY_ACTIONS.SET_SEND_MONEY_LIMITS,
    payload,
  };
}

/**
 * Caches the GetSendMoneyLimits content (by quote or by country), keyed by
 * request URL, so the limits modal and the wallet modal share one fetch.
 */
export const SetSendMoneyLimitsContent = (payload: SendMoneyLimitsContentCache) => ({
  type: SEND_MONEY_ACTIONS.SET_SEND_MONEY_LIMITS_CONTENT,
  payload,
});

/**
 * Sets the send money transfer type for the send money flow
 */
export const SetSendMoneyTransferMethod = (payload: SendMoneyTransferMethodId) => ({
  type: SEND_MONEY_ACTIONS.SET_SEND_MONEY_TRANSFER_METHOD,
  payload,
});

/**
 * Sets the destination country + currency and persists them to sessionStorage
 * so the quote flow survives a refresh.
 */
export const SetSendMoneyCountryCurrency = (payload: {
  country: string;
  currency: string;
}) => {
  if (!payload.country && !payload.currency) {
    clearCountryCurrency();
  } else {
    saveCountryCurrency(payload);
  }
  return {
    type: SEND_MONEY_ACTIONS.SET_SEND_MONEY_COUNTRY_CURRENCY,
    payload,
  };
};

/**
 * Persists the quote identifiers needed by downstream flow calls (workflow
 * fetch, continue). Null when the quote is cleared.
 */
export const SetSendMoneyQuote = (payload: {
  quoteId: number | null;
  transactionContextId: string | null;
}) => ({
  type: SEND_MONEY_ACTIONS.SET_SEND_MONEY_QUOTE,
  payload,
});

/**
 * Sets the selected mobile wallet operator for the send money flow. Held in
 * Redux because the workflow API call downstream needs it.
 */
export const SetSelectedOperator = (payload: MobileWalletOperator | null) => ({
  type: SEND_MONEY_ACTIONS.SET_SELECTED_OPERATOR,
  payload,
});

/**
 * Resets the whole Send Money slice and clears everything it persisted to
 * sessionStorage (e.g. on logout).
 */
export function ResetSendMoney() {
  clearSendMoneyPersistence();
  return {
    type: SEND_MONEY_ACTIONS.RESET_SEND_MONEY,
  };
}

export type SendMoneyAction =
  | ReturnType<typeof SetSelectedRecipient>
  | ReturnType<typeof SetSendMoneyTransferMethod>
  | ReturnType<typeof SetSendMoneyLimits>
  | ReturnType<typeof SetSendMoneyLimitsContent>
  | ReturnType<typeof SetSendMoneyCountryCurrency>
  | ReturnType<typeof SetSendMoneyQuote>
  | ReturnType<typeof SetSelectedOperator>
  | ReturnType<typeof ResetSendMoney>